import type {FC, KeyboardEvent} from "react";
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {Ad, MediaCrop} from "../../../client/api";
import {AdPositionEnum} from "../../../client/api";
import {calcMediaStyle} from "../../../config/mediaUtils";

type InternalAdRendererProps = {
  ad: Ad;
  position: AdPositionEnum;
  className?: string;
};

type LayoutProps = InternalAdRendererProps & {
  onClick: () => void;
};

type AdLayoutComponent = FC<LayoutProps>;

const baseContainerClasses =
  "internal-ad-base w-full rounded-2xl overflow-hidden cursor-pointer border border-black/10 bg-black/5 block";

const homeMatchClasses =
  "internal-ad-home-match w-full rounded-xl overflow-hidden cursor-pointer bg-[linear-gradient(135deg,rgba(8,25,48,0.8),rgba(17,45,86,0.8))] block";

const leagueMatchClasses =
  "internal-ad-league-match w-full rounded-xl overflow-hidden cursor-pointer border border-slate-900/10 bg-slate-900/5 block";

const DEFAULT_MEDIA_CROP: MediaCrop = Object.freeze({
  cropScale: 1,
  cropX: 0,
  cropY: 0,
  cropAspectRatio: 1,
});

const buildClassName = (
  baseClass: string,
  extra?: string,
): string | undefined =>
  [baseClass, extra]
    .filter((value) => typeof value === "string" && value.trim().length > 0)
    .join(" ")
    .trim() || undefined;

const BaseAdLayout: AdLayoutComponent = ({
  ad,
  className,
  onClick,
  position,
}) => (
  <AdContainer
    ad={ad}
    className={buildClassName(baseContainerClasses, className)}
    onClick={onClick}
    position={position}
  />
);

const HomeMatchAdLayout: AdLayoutComponent = ({
  ad,
  className,
  onClick,
  position,
}) => (
  <AdContainer
    ad={ad}
    className={buildClassName(homeMatchClasses, className)}
    onClick={onClick}
    position={position}
  />
);

const LeagueMatchAdLayout: AdLayoutComponent = ({
  ad,
  className,
  onClick,
  position,
}) => (
  <AdContainer
    ad={ad}
    className={buildClassName(leagueMatchClasses, className)}
    onClick={onClick}
    position={position}
  />
);

const layoutMap: Partial<Record<AdPositionEnum, AdLayoutComponent>> = {
  [AdPositionEnum.HOME_MATCH]: HomeMatchAdLayout,
  [AdPositionEnum.HOME_MATCH_DOUBLE]: HomeMatchAdLayout,
  [AdPositionEnum.LEAGUE_MATCH_MATCH]: LeagueMatchAdLayout,
};

const INTERNAL_APPLINK_TARGET = "https://hello.clava-sports.com/";

const normalizeAdUrl = (rawUrl?: string | null): string | null => {
  const trimmedUrl = rawUrl?.trim();
  if (!trimmedUrl) {
    return null;
  }
  return trimmedUrl.toLowerCase().startsWith("inapplink://")
    ? INTERNAL_APPLINK_TARGET
    : trimmedUrl;
};

export const InternalAdRenderer: FC<InternalAdRendererProps> = ({
  ad,
  position,
  className,
}) => {
  const LayoutComponent = layoutMap[position] ?? BaseAdLayout;

  const normalizedUrl = useMemo(() => normalizeAdUrl(ad?.url), [ad?.url]);

  const handleOpen = useCallback(() => {
    if (!normalizedUrl) {
      return;
    }
    window.open(normalizedUrl, "_blank", "noopener,noreferrer");
  }, [normalizedUrl]);

  return (
    <LayoutComponent
      ad={ad}
      position={position}
      className={className}
      onClick={handleOpen}
    />
  );
};

type AdContainerProps = {
  ad: Ad;
  className?: string;
  onClick: () => void;
  position: AdPositionEnum;
};

const AdContainer: FC<AdContainerProps> = ({
  ad,
  className,
  onClick,
  position,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }

    const updateWidth = () => {
      setContainerWidth(node.getBoundingClientRect().width);
    };

    updateWidth();

    let cleanup: () => void;

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      observer.observe(node);
      cleanup = () => observer.disconnect();
    } else {
      window.addEventListener("resize", updateWidth);
      cleanup = () => window.removeEventListener("resize", updateWidth);
    }

    return cleanup;
  }, []);

  const imageSources = useMemo(() => {
    const desktopImage = ad?.imageDesktop;
    const mobileImage = ad?.imageMobile;
    const referenceImage = desktopImage ?? mobileImage ?? null;
    const desktop = desktopImage?.url ?? mobileImage?.url ?? "";
    const mobile = mobileImage?.url ?? desktop;
    const cropAspectRatio =
      referenceImage?.cropAspectRatio ?? referenceImage?.aspectRatio ?? null;

    return {
      desktop,
      mobile,
      referenceImage,
      cropAspectRatio,
    };
  }, [ad]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      ref={containerRef}
      className={buildClassName("relative overflow-hidden", className)}
      style={
        imageSources.cropAspectRatio
          ? {aspectRatio: imageSources.cropAspectRatio}
          : undefined
      }
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-ad-position={position}
      aria-label={ad?.name ?? "Advertisement"}
    >
      <AdPicture
        ad={ad}
        sources={imageSources}
        containerWidth={containerWidth}
      />
    </div>
  );
};

type AdPictureProps = {
  ad: Ad;
  sources: {
    desktop: string;
    mobile: string;
    referenceImage: Ad["imageDesktop"] | Ad["imageMobile"] | null;
    cropAspectRatio: number | null;
  };
  containerWidth: number;
};

const AdPicture: FC<AdPictureProps> = ({ad, sources, containerWidth}) => {
  const imageStyle = useMemo<React.CSSProperties>(() => {
    const reference = sources.referenceImage;
    if (!reference || !containerWidth || !reference.aspectRatio) {
      return calcMediaStyle(containerWidth || 1, 1, DEFAULT_MEDIA_CROP);
    }
    return calcMediaStyle(containerWidth, reference.aspectRatio, reference);
  }, [containerWidth, sources.referenceImage]);

  if (!sources.desktop && !sources.mobile) {
    return null;
  }

  return (
    <picture className="block h-full w-full">
      {sources.mobile && (
        <source media="(max-width: 767px)" srcSet={sources.mobile} />
      )}
      <img
        src={sources.desktop || sources.mobile}
        alt={ad?.name ?? "Advertisement"}
        loading="lazy"
        className="h-full w-full object-cover"
        style={imageStyle}
      />
    </picture>
  );
};

